import { useState, useEffect } from "react";
import {
  Plus,
  Search,
  CheckSquare,
  TrendingUp,
  Clock,
  User,
  Calendar,
  Check,
  Filter,
  Edit2,
  Trash2,
  MoreVertical,
  List,
  ArrowRight,
  ArrowLeft,
} from "react-feather";
import { useOutletContext, useNavigate } from "react-router-dom";
import { Box } from "../../components/Box";
import { Button } from "../../components/Button";
import { DropdownMenu } from "../../components/DropdownMenu";
import { Colors } from "../../values/colors";
import { Text } from "../../components/BaseComponents/styles";
import { LoadingOver } from "../../components/LoadingOver";
import { ActionPlanModal } from "./components/ActionPlanModal";
import ActionPlansService from "../../services/ActionPlans";
import { toast } from "react-toastify";
import { formatDateToDisplay } from "../../utils/date";
import graduationIcon from "../../assets/icons/graduation.svg";
import {
  PageBody,
  StatsRow,
  StatCard,
  StatIconCircle,
  StatInfo,
  StatLabel,
  StatValue,
  FiltersBar,
  SearchWrapper,
  SearchInput,
  SearchBtn,
  FilterLabel,
  FilterSelect,
  SectionHeading,
  SectionDot,
  SectionCount,
  CardsGrid,
  ActionCard,
  CardTop,
  PriorityBadge,
  CardTitle,
  CardCategory,
  CardDescription,
  CardMeta,
  CardDivider,
  CardActions,
  PracticedBtn,
  TrackBtn,
  MenuItem,
  EmptyWrapper,
  EmptyIconCircle,
  EmptyTitle,
  EmptySubtext,
  CardTitleDesc,
  GraduationIcon,
} from "./styles";
import { BackBtn } from "../PsciossocialRisk/SurveyDetail/styles";

const URGENCY_CONFIG = {
  critical: { label: "Crítica", color: Colors.red },
  high: { label: "Alta", color: Colors.orange },
  medium: { label: "Média", color: "#F0B429" },
};

const toastError = (error) =>
  toast.error(error?.response?.data?.message || error?.message || "Ocorreu um erro inesperado.");

const ActionCardMenu = ({ plan, onEdit, onDelete }) => (
  <>
    <Text size={13} weight={700} style={{ margin: "10px 14px 4px", display: "block" }}>
      Ações
    </Text>
    {!plan.action_completed && (
      <MenuItem onClick={onEdit}>
        <Edit2 size={14} />
        Editar ação
      </MenuItem>
    )}
    <MenuItem variant="danger" onClick={onDelete}>
      <Trash2 size={14} />
      Deletar
    </MenuItem>
  </>
);

const PlanCard = ({ plan, onEdit, onDelete, onToggleCompleted, onTrack }) => {
  const urgency = URGENCY_CONFIG[plan.priority] || URGENCY_CONFIG.medium;
  const authorName = plan.user?.name ?? "—";
  const categoryName = plan.action_category?.name ?? "—";
  const dueDate = plan.due_date
    ? formatDateToDisplay(plan.due_date, "DD MMM YY")
    : "—";

  return (
    <ActionCard>
      <PriorityBadge bg={urgency.color}>{urgency.label}</PriorityBadge>
      <CardTitleDesc>
        <CardTop>
          <GraduationIcon>
            <img src={graduationIcon} alt="PGR - Plano de Ação" />
          </GraduationIcon>
          <div style={{ marginLeft: 10, display: "flex", flexDirection: "column" }}>
            <CardTitle>{plan.title}</CardTitle>
            <CardCategory>{categoryName}</CardCategory>
          </div>
        </CardTop>
        <DropdownMenu icon={<MoreVertical size={18} />}>
          <ActionCardMenu
            plan={plan}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        </DropdownMenu>
      </CardTitleDesc>

      <CardDescription>{plan.description}</CardDescription>

      <CardMeta>
        <User size={12} />
        <span>{authorName}</span>
        {dueDate !== "—" && (
          <>
            <Calendar size={12} style={{ marginLeft: 4 }} />
            <span>{dueDate}</span>
          </>
        )}
      </CardMeta>

      <CardDivider />

      <CardActions>
        <PracticedBtn $active={plan.action_completed} onClick={() => onToggleCompleted(plan)}>
          <Check size={13} />
          Praticada
        </PracticedBtn>
        <TrackBtn onClick={() => onTrack(plan)}>
          <List size={13} />
          Acompanhar
        </TrackBtn>
      </CardActions>
    </ActionCard>
  );
};

export const ActionPlans = () => {
  const [data, setData] = useState({ action_plans: [], total: 0, onGoing: 0, overdue: 0, finalized: 0 });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ open: false, mode: null, plan: null });
  const [setOptions] = useOutletContext();
  const navigate = useNavigate();

  const openModal = (mode, plan = null) => setModal({ open: true, mode, plan });
  const closeModal = () => setModal({ open: false, mode: null, plan: null });

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      const result = await ActionPlansService.list(params);
      setData(result || { action_plans: [], total: 0, onGoing: 0, overdue: 0, finalized: 0 });
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, [statusFilter, priorityFilter]);

  const plans = data.action_plans || [];

  useEffect(() => {
    setOptions({
      start: (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <BackBtn onClick={() => navigate('/psciossocial-risk')}>
            <ArrowLeft size={18} color="#fff" />
          </BackBtn>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Text 
              color="rgba(255,255,255,0.7)" 
              weight={700} 
              style={{ textDecoration: "underline", marginLeft: 12 }}>
              Plano de Ações - PGR
            </Text>
            <Text color="white" size={13} style={{marginLeft: 12 }}>
              {plans.length} ações encontradas
            </Text>
          </div>
        </div>
      ),
      end: [
        <Button
          color={Colors.primary}
          icon={<Plus size={18} />}
          onClick={() => openModal("create")}
          rounded
        >
          Nova Ação
        </Button>,
      ],
    });
    return () => setOptions(null);
  }, [plans.length]);

  const handleCreate = async (payload) => {
    try {
      await ActionPlansService.create(payload);
      await fetchPlans();
      toast.success("Ação criada com sucesso!");
    } catch (error) {
      toastError(error);
    }
  };

  const handleSave = async (updated) => {
    try {
      await ActionPlansService.update(updated.id, updated);
      await fetchPlans();
      toast.success("Ação atualizada com sucesso!");
    } catch (error) {
      toastError(error);
    }
  };

  const handleDelete = async (plan) => {
    try {
      await ActionPlansService.remove(plan.id);
      await fetchPlans();
      toast.success("Ação removida.");
    } catch (error) {
      toastError(error);
    }
  };

  const handleToggleCompleted = async (plan) => {
    try {
      await ActionPlansService.toggleCompleted(plan.id);
      await fetchPlans();
    } catch (error) {
      toastError(error);
    }
  };

  const handleTrack = (plan) => {
    navigate(`/action-plan/${plan.id}`, { state: { plan } });
  };

  const filtered = plans.filter((p) =>
    !search.trim() || p.title?.toLowerCase().includes(search.toLowerCase())
  );

  const byNewest = (a, b) => new Date(b.created_at) - new Date(a.created_at);
  const inProgress = filtered.filter((p) => !p.action_completed && p.status === "ongoing").sort(byNewest);
  const practiced = filtered.filter((p) => p.action_completed).sort(byNewest);
  const overdue = filtered.filter((p) => p.status === "overdue").sort(byNewest);

  return (
    <>
      <Box noPadding noHeader style={{ minHeight: "80vh" }}>
        <PageBody>
          <StatsRow>
            <StatCard>
              <StatIconCircle bg="rgba(93,61,237,0.1)">
                <List size={18} />
              </StatIconCircle>
              <StatInfo>
                <StatLabel>Total de Ações</StatLabel>
                <StatValue>{data.total}</StatValue>
              </StatInfo>
            </StatCard>

            <StatCard>
              <StatIconCircle bg="rgba(255,160,0,0.1)">
                <TrendingUp size={18} />
              </StatIconCircle>
              <StatInfo>
                <StatLabel>Em Andamento</StatLabel>
                <StatValue>{data.onGoing}</StatValue>
              </StatInfo>
            </StatCard>

            <StatCard>
              <StatIconCircle bg="rgba(250,51,51,0.1)">
                <Clock size={18} />
              </StatIconCircle>
              <StatInfo>
                <StatLabel>Atrasadas</StatLabel>
                <StatValue>{data.overdue}</StatValue>
              </StatInfo>
            </StatCard>

            <StatCard>
              <StatIconCircle bg="rgba(45,206,159,0.1)">
                <CheckSquare size={18} />
              </StatIconCircle>
              <StatInfo>
                <StatLabel>Concluídas</StatLabel>
                <StatValue>{data.finalized}</StatValue>
              </StatInfo>
            </StatCard>
          </StatsRow>

          <FiltersBar>
            <SearchWrapper>
              <Search size={15} color={Colors.lightGray} />
              <SearchInput
                placeholder="Buscar ação..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchPlans()}
              />
            </SearchWrapper>
            <SearchBtn onClick={fetchPlans}>
              <ArrowRight size={15} color="#fff" />
            </SearchBtn>

            <FilterLabel>
              <Filter size={14} />
              Filtros:
            </FilterLabel>

            <FilterSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">Todos os Status</option>
              <option value="ongoing">Em Andamento</option>
              <option value="overdue">Atrasadas</option>
              <option value="finalized">Concluídas</option>
            </FilterSelect>

            <FilterSelect value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
              <option value="">Todas Prioridades</option>
              <option value="critical">Crítica</option>
              <option value="high">Alta</option>
              <option value="medium">Média</option>
            </FilterSelect>
          </FiltersBar>

          {loading ? (
            <LoadingOver />
          ) : plans.length === 0 ? (
            <EmptyWrapper>
              <EmptyIconCircle>
                <CheckSquare size={36} color={Colors.primaryPurple} />
              </EmptyIconCircle>
              <EmptyTitle>Nenhuma ação disponível</EmptyTitle>
              <EmptySubtext>
                Você ainda não tem nenhuma ação cadastrada. Gostaria de criar uma nova ação?
              </EmptySubtext>
              <Button
                color={Colors.primary}
                icon={<Plus size={18} />}
                style={{ marginTop: 8 }}
                onClick={() => openModal("create")}
              >
                Nova Ação
              </Button>
            </EmptyWrapper>
          ) : (
            <>
              {inProgress.length > 0 && (
                <>
                  <SectionHeading>
                    <SectionDot color={Colors.orange} />
                    Em Andamento
                    <SectionCount>{inProgress.length}</SectionCount>
                  </SectionHeading>
                  <CardsGrid>
                    {inProgress.filter((plan) => plan.status === "ongoing").map((plan) => (
                      <PlanCard
                        key={plan.id}
                        plan={plan}
                        onEdit={() => openModal("edit", plan)}
                        onDelete={() => openModal("confirmDelete", plan)}
                        onToggleCompleted={(p) => !p.action_completed && openModal("confirmPracticed", p)}
                        onTrack={handleTrack}
                      />
                    ))}
                  </CardsGrid>
                </>
              )}

              {practiced.length > 0 && (
                <>
                  <SectionHeading>
                    <SectionDot color={Colors.secondaryGreen} />
                    Praticadas
                    <SectionCount>{practiced.length}</SectionCount>
                  </SectionHeading>
                  <CardsGrid>
                    {practiced.map((plan) => (
                      <PlanCard
                        key={plan.id}
                        plan={plan}
                        onEdit={() => openModal("edit", plan)}
                        onDelete={() => openModal("confirmDelete", plan)}
                        onToggleCompleted={(p) => !p.action_completed && openModal("confirmPracticed", p)}
                        onTrack={handleTrack}
                      />
                    ))}
                  </CardsGrid>
                </>
              )}

              {overdue.length > 0 && (
                <>
                  <SectionHeading>
                    <SectionDot color={Colors.red} />
                    Em Atraso
                    <SectionCount>{overdue.length}</SectionCount>
                  </SectionHeading>
                  <CardsGrid>
                    {overdue.map((plan) => (
                      <PlanCard
                        key={plan.id}
                        plan={plan}
                        onEdit={() => openModal("edit", plan)}
                        onDelete={() => openModal("confirmDelete", plan)}
                        onToggleCompleted={(p) => !p.action_completed && openModal("confirmPracticed", p)}
                        onTrack={handleTrack}
                      />
                    ))}
                  </CardsGrid>
                </>
              )}
            </>
          )}
        </PageBody>
      </Box>

      <ActionPlanModal
        open={modal.open}
        mode={modal.mode}
        plan={modal.plan}
        onClose={closeModal}
        onCreate={handleCreate}
        onSave={handleSave}
        onDelete={handleDelete}
        onConfirmPracticed={() => handleToggleCompleted(modal.plan)}
      />
    </>
  );
};
